import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Skeleton,
  TablePagination,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ProgressBar } from '@/components/common/ProgressBar'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageBreadcrumbs } from '@/components/common/PageBreadcrumbs'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { programAssignmentsApi, type ProgramAssignment } from '@/api/programs'
import { getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'

/**
 * Программы ученика: активные и завершённые. Пройденные программы остаются
 * доступными — по ним можно повторять материал.
 */
export function MyProgramsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [assignments, setAssignments] = useState<ProgramAssignment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await programAssignmentsApi.list({ page: page + 1, per_page: perPage })
      setAssignments(result.data)
      setTotal(result.meta?.total ?? result.data.length)
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, perPage, t])

  useEffect(() => {
    void load()
  }, [load])

  // Страница за пределами выдачи: программу завершили и убрали, размер страницы
  // увеличили, либо номер остался с прошлого визита. Ученику такая страница
  // приходит пустой и читается как «программ нет вовсе», поэтому возвращаемся
  // на последнюю существующую сами.
  useEffect(() => {
    if (loading || page === 0 || page * perPage < total) return

    setPage(total === 0 ? 0 : Math.ceil(total / perPage) - 1)
  }, [loading, page, perPage, total])

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <PageBreadcrumbs items={[{ label: t('programs.my.title') }]} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('programs.my.title', 'Моя программа')}
          </Typography>
          <PageHelpPopover pageKey="my-programs" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
        ) : assignments.length === 0 ? (
          // «Программ нет» — только когда их действительно нет во всей выдаче.
          // Пустая страница за пределами total — это промах номера страницы, и
          // сказать про неё «преподаватель не назначил программу» значит соврать
          // ученику; страницу вернёт эффект-кламп выше.
          total === 0 ? (
            <Card>
              <CardContent>
                <Typography color="text.secondary">
                  {t('programs.my.empty', 'Преподаватель пока не назначил программу обучения.')}
                </Typography>
              </CardContent>
            </Card>
          ) : null
        ) : (
          assignments.map((assignment) => {
            // Не путать с `total` выдачи выше: здесь счёт тем внутри программы
            const themesTotal = assignment.group_progress?.themes_total ?? assignment.themes?.length ?? 0
            // Личный прогресс важнее группового: тема, пройденная группой без
            // ученика, его достижением не стала
            const done = assignment.personal?.themes_done
              ?? assignment.group_progress?.themes_done
              ?? (assignment.themes ?? []).filter((theme) => theme.status === 'done').length
            const percent = themesTotal > 0 ? (done / themesTotal) * 100 : 0

            return (
              <Card key={assignment.id} sx={{ mb: 2 }}>
                <CardActionArea onClick={() => navigate(ROUTES.MY_PROGRAM_VIEW(assignment.id))}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">{assignment.name}</Typography>
                    {assignment.status === 'paused' && (
                      <Chip size="small" variant="outlined" label={t('programs.status.paused', 'на паузе')} />
                    )}
                    {assignment.status === 'completed' && (
                      <Chip size="small" color="success" label={t('programs.status.completed', 'завершена')} />
                    )}
                    {assignment.recipient.type === 'group' && (
                      <Chip size="small" label={t('programs.group', 'группа')} />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('programs.my.progress', '{{done}} из {{total}} тем', { done, total: themesTotal })}
                  </Typography>
                  <ProgressBar value={percent} sx={{ mt: 1 }} />
                </CardContent>
                </CardActionArea>
              </Card>
            )
          })
        )}

        {/* Пагинация смонтирована всегда — и под скелетоном, и над пустой
            страницей. Раньше контрол показывался только при `total > perPage`:
            ученик с двадцатью программами ставил размер страницы 50, условие
            становилось ложным, и выбор размера исчезал вместе с контролом —
            вернуть 15 было нечем без перезагрузки. Под скелетоном её тоже не
            прячем: перелистывание не должно вырывать контрол из-под курсора. */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, overflow: 'hidden', minWidth: 0 }}>
          <TablePagination
            component="div"
            count={total}
            // Номер в пределах выдачи: до неё страницу догоняет эффект-кламп, а
            // MUI на page за пределами count ругается в консоль
            page={total === 0 ? 0 : Math.min(page, Math.ceil(total / perPage) - 1)}
            onPageChange={(_, value) => setPage(value)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => {
              setPerPage(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 15, 25, 50]}
            labelRowsPerPage={isMobile ? '' : t('common.rowsPerPage', 'Строк на странице')}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} ${t('common.of', 'из')} ${count}`}
          />
        </Box>
      </Box>
    </DashboardLayout>
  )
}
